// src/screens/s2/S2StandingOrders.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 14 — Standing Orders for S2.
// Grouped, no charts. Business-account SOs (employer pension, CT reserve,
// VAT reserve conditional); director drawings (salary, dividends); DLA plan
// conditional on sign; target dates for debtor collection, business EF,
// historic liability clearance, debt free.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { formatCurrency, formatMonths } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

function OrderLine({ label, amount, recipient, note }: { label: string; amount: string; recipient: string; note?: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-4 py-3 border-b border-gray-100 last:border-0 items-baseline">
      <div>
        <div className="text-sm font-medium text-gray-800">{label}</div>
        {note && <div className="text-xs text-gray-500 mt-0.5">{note}</div>}
      </div>
      <div className="text-sm text-gray-500">→ {recipient}</div>
      <div className="text-lg font-bold text-ma-navy">{amount}</div>
    </div>
  );
}

function Section({ title, colour, children }: { title: string; colour: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border-l-4 border border-gray-200 p-5 mb-4" style={{ borderLeftColor: colour }}>
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: colour }}>{title}</h3>
      {children}
    </div>
  );
}

function TargetLine({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="flex justify-between items-baseline py-2 border-b border-gray-100 last:border-0">
      <div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
      <div className="text-sm font-semibold text-ma-navy">{value}</div>
    </div>
  );
}

const addMonths = (d: Date, m: number) => {
  const r = new Date(d);
  r.setMonth(r.getMonth() + m);
  return r;
};
const ymd = (d: Date) => d.toISOString().slice(0, 10);

export default function S2StandingOrders({ data }: Props) {
  const pensionMonthly = data.s2PensionMonthly ?? 0;
  const ctReserveMonthly = data.s2CtReserveMonthly ?? 0;
  const vatReserveMonthly = data.s2VatReserveMonthly ?? 0;
  const dividendMonthly = data.s2DividendMonthly ?? 0;
  const salaryMonthly = (data.salary || 0) / 12;

  const dlaBalance = data.businessDlaBalance ?? 0;
  const dlaOverdrawn = dlaBalance < 0;
  const dlaCredit = dlaBalance > 0;
  const route = data.s2DlaRepaymentRoute;
  const dlaRepayment = data.s2DlaMonthlyRepayment ?? 0;

  // Target dates — compute from today using the clearance / EF months stored.
  const today = new Date();
  const clearanceMonths = data.s2HistoricCtLiabilityClearanceMonths ?? 12;
  const clearanceDate = ymd(addMonths(today, clearanceMonths));

  // EF completion via capital-allocation heuristic — dependent on remaining cash.
  const efTargetMonths = data.s2BusinessEfTarget ?? 3;
  const efDateLabel = efTargetMonths > 0 ? `${efTargetMonths}mo cover target — see Capital Allocation` : '—';

  const debtorTarget = data.s2DebtorCollectionTarget ?? 0;
  const debtorDate = data.s2DebtorCollectionDate || '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Session 2 standing orders</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Every commitment agreed today — set these up before you leave.
        </p>
      </div>

      {/* BUSINESS ACCOUNT */}
      <Section title="From business account" colour="#1E3A5F">
        <OrderLine
          label="Employer pension contribution"
          amount={`${formatCurrency(pensionMonthly)}/mo`}
          recipient={data.pensionProvider || 'Pension provider'}
          note="CT-deductible — reduces company tax at effective rate"
        />
        <OrderLine
          label="CT reserve"
          amount={`${formatCurrency(ctReserveMonthly)}/mo`}
          recipient="Dedicated tax reserve account"
          note="Covers the next CT bill month-by-month"
        />
        {vatReserveMonthly > 0 && (
          <OrderLine
            label="VAT reserve"
            amount={`${formatCurrency(vatReserveMonthly)}/mo`}
            recipient="VAT reserve account"
            note="Covers quarterly VAT liability"
          />
        )}
      </Section>

      {/* DIRECTOR DRAWINGS */}
      <Section title="Director drawings" colour="#C9A84C">
        <OrderLine
          label="Salary"
          amount={`${formatCurrency(salaryMonthly)}/mo`}
          recipient="Personal account"
          note="Set in Session 1"
        />
        <OrderLine
          label="Dividends"
          amount={`${formatCurrency(dividendMonthly)}/mo`}
          recipient="Personal account"
          note="Set today"
        />
      </Section>

      {/* DLA PLAN (conditional) */}
      {dlaOverdrawn && (
        <Section title="DLA plan — overdrawn" colour="#F97316">
          <OrderLine
            label="DLA repayment"
            amount={route === 'dividend' ? 'One-off dividend' : `${formatCurrency(dlaRepayment)}/mo`}
            recipient="Company director loan account"
            note={
              route === 'dividend'
                ? 'Dividend route — gross up, personal tax paid'
                : route === 'formal'
                  ? 'Formal route — transfer from personal to company monthly'
                  : 'Repayment route not yet selected — see Capital Allocation'
            }
          />
        </Section>
      )}
      {dlaCredit && (
        <Section title="DLA plan — credit" colour="#10B981">
          <OrderLine
            label="DLA deployment"
            amount={formatCurrency(dlaBalance)}
            recipient="Company director loan account"
            note={
              route === 'dividend' ? 'Extract tax-free — draw down as loan repayment'
                : route === 'credit-reduce-dividends' ? 'Reduce future dividends'
                : 'Leave in company as working capital'
            }
          />
        </Section>
      )}

      {/* TARGETS */}
      <Section title="Target dates" colour="#60A5FA">
        <TargetLine
          label="Debtor collection"
          value={debtorTarget > 0 ? `${debtorTarget}% by ${debtorDate}` : '—'}
          subtitle="% of aged debtors to recover"
        />
        <TargetLine
          label="Business EF complete"
          value={efDateLabel}
          subtitle={`${efTargetMonths} months fixed-cost cover`}
        />
        <TargetLine
          label="Historic liabilities cleared"
          value={clearanceDate}
          subtitle={`${formatMonths(clearanceMonths)} months from today — CT + VAT + PAYE`}
        />
        <TargetLine
          label="Debt free"
          value="See Balance Sheet Projection"
          subtitle="Historic liabilities + long-term debt fully cleared"
        />
      </Section>
    </div>
  );
}
